import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react"
import { isEmpty } from "lodash"
import { useLastIntradayBatch } from "hooks/intraday"
import { DataStatus, OK } from "helpers/data-statuses"
import { getToday, IsoDate } from "helpers/types/dates"
import { IntradayBatchMetadata } from "model/state"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "../context/dashboard-id"
import { DEFAULT_CORE_STATE } from "../store/core-slice"

/**
 * Helper function to calculate intraday state
 * @param isIntraday - Base intraday flag
 * @param endDate - Current end date
 * @param intradayDate - Today's date (intraday date)
 * @param lastIntradayBatch - Intraday batch data
 * @param intradayBatchStatus - Status of intraday batch
 */
export function calculateIntradayState(
  isIntraday: boolean,
  endDate: IsoDate,
  intradayDate: IsoDate,
  lastIntradayBatch: IntradayBatchMetadata | null,
  intradayBatchStatus: DataStatus,
): [{
  isIntradayMode: boolean
  intradayDataAvailable: boolean
  isEndDateIntraday: boolean
  lastIntradayBatchId: string | null
}, DataStatus] {
  const intradayDataAvailable = !isEmpty(lastIntradayBatch) && intradayBatchStatus === OK
  const isEndDateIntraday = endDate === intradayDate
  const isIntradayMode = isIntraday && isEndDateIntraday

  return [
    {
      isIntradayMode,
      intradayDataAvailable,
      isEndDateIntraday,
      lastIntradayBatchId: lastIntradayBatch?.id ?? null,
    },
    intradayBatchStatus,
  ]
}

export function useIsIntradayMode(): boolean {
  const dashboardId = useDashboardId()
  const coreState = useAppSelector((s) => s.dashboard.core[dashboardId] ?? DEFAULT_CORE_STATE)
  return coreState.isIntraday && coreState.endDate === getToday()
}

export function useIntradayState() {
  const dashboardId = useDashboardId()
  const coreState = useAppSelector((s) => s.dashboard.core[dashboardId] ?? DEFAULT_CORE_STATE)
  const { isIntraday, endDate, instrument, instrumentsMetadata } = coreState
  const [lastIntradayBatch, intradayBatchStatus] = useLastIntradayBatch(instrument, instrumentsMetadata?.original?.intradayEnabled)
  const today = getToday()

  // useLastIntradayBatch blips null for one render per live-refresh tick; without latching,
  // isIntradaySimulationEligible would misread that as a stale batchId. Read via
  // useSyncExternalStore, not ref.current, since refs are write-only during render.
  const lastKnownIntradayBatchRef = useRef<IntradayBatchMetadata | null>(null)
  const getLastKnownIntradayBatch = useCallback(() => lastKnownIntradayBatchRef.current, [])
  const subscribeToLastKnownIntradayBatch = useCallback(() => () => undefined, [])
  const latchedLastIntradayBatch = useSyncExternalStore(subscribeToLastKnownIntradayBatch, getLastKnownIntradayBatch)

  useEffect(() => {
    if (intradayBatchStatus === OK) {
      lastKnownIntradayBatchRef.current = lastIntradayBatch
    }
  }, [intradayBatchStatus, lastIntradayBatch])

  const stableLastIntradayBatch = intradayBatchStatus === OK ? lastIntradayBatch : latchedLastIntradayBatch

  return useMemo(() => {
    return calculateIntradayState(isIntraday, endDate, today, stableLastIntradayBatch, intradayBatchStatus)
  }, [isIntraday, endDate, today, stableLastIntradayBatch, intradayBatchStatus])
}
